import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getVolatilityDetail } from '../api/metrics';
import { getDefaultDateRange } from '../utils/dateRangeDefaults';
import { useDateRange, useDateRangeStore } from '../stores/useDateRangeStore';
import { formatDollar } from '../utils/currencyFormatter';
import StandaloneSection from './shared/StandaloneSection';
import GranularityDateRangeControls, { TIMEFRAMES, TimeframeKey } from './shared/GranularityDateRangeControls';
import './PriceIntelligencePanel.css';

interface PriceIntelligencePanelProps {
  productId: string;
}


const PriceIntelligencePanel: React.FC<PriceIntelligencePanelProps> = ({ productId }) => {
  // Timeframe state
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeKey>('15m');
  const granularity = useMemo(() => TIMEFRAMES[selectedTimeframe].granularityMins, [selectedTimeframe]);

  // Date range from store
  const { dateRange } = useDateRange();
  const { setCustomRange } = useDateRangeStore();

  const startDate = dateRange.start_date;
  const endDate = dateRange.end_date;

  // Initialize date range on mount and when granularity changes
  const handleTimeframeChange = (newTimeframe: TimeframeKey) => {
    setSelectedTimeframe(newTimeframe);
    const newGranularity = TIMEFRAMES[newTimeframe].granularityMins;
    const range = getDefaultDateRange(newGranularity);
    setCustomRange({
      start_date: range.startTime.split('T')[0],
      end_date: range.endTime.split('T')[0]
    });
  };

  useEffect(() => {
    const range = getDefaultDateRange(granularity);
    setCustomRange({
      start_date: range.startTime.split('T')[0],
      end_date: range.endTime.split('T')[0]
    });
  }, []); // Only on mount

  // Fetch data for all three percentiles (25, 50, 75)
  const { data: data25, isLoading: isLoading25, error: error25 } = useQuery({
    queryKey: ['priceIntelligence', productId, startDate, endDate, granularity, 25],
    queryFn: () => getVolatilityDetail(productId, startDate, endDate, granularity, 25),
    staleTime: 60000,
    retry: 2,
  });

  const { data: data50, isLoading: isLoading50, error: error50 } = useQuery({
    queryKey: ['priceIntelligence', productId, startDate, endDate, granularity, 50],
    queryFn: () => getVolatilityDetail(productId, startDate, endDate, granularity, 50),
    staleTime: 60000,
    retry: 2,
  });

  const { data: data75, isLoading: isLoading75, error: error75 } = useQuery({
    queryKey: ['priceIntelligence', productId, startDate, endDate, granularity, 75],
    queryFn: () => getVolatilityDetail(productId, startDate, endDate, granularity, 75),
    staleTime: 60000,
    retry: 2,
  });

  const isLoading = isLoading25 || isLoading50 || isLoading75;
  const isError = error25 || error50 || error75;
  const error = error25 || error50 || error75;

  const metrics25 = data25?.percentile_metrics;
  const metrics50 = data50?.percentile_metrics;
  const metrics75 = data75?.percentile_metrics;

  return (
    <div className="price-intelligence-panel">
      {/* Controls - Always Visible */}
      <GranularityDateRangeControls
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        isLoading={isLoading}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading price intelligence data...</p>
        </div>
      )}

      {/* Error State */}
      {!isLoading && isError && (
        <StandaloneSection title="ERROR">
          <div className="error-state">
            <p className="error-message">
              Failed to load price intelligence: {error instanceof Error ? error.message : 'Unknown error'}
            </p>
          </div>
        </StandaloneSection>
      )}

      {/* No Data State */}
      {!isLoading && !isError && (!metrics25 || !metrics50 || !metrics75) && (
        <StandaloneSection title="NO DATA">
          <div className="error-state">
            <p className="error-message">No price data available for the selected period.</p>
          </div>
        </StandaloneSection>
      )}

      {/* Data Sections - Only show when we have data */}
      {!isLoading && !isError && metrics25 && metrics50 && metrics75 && (() => {
        const winRate = metrics50.gain_frequency_pct;
        return (
        <>
          {/* Summary Card at Top */}
          <StandaloneSection title="MARKET SUMMARY">
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Current Price:</span>
            <span className="summary-value">{formatDollar(metrics50.current_price, productId)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Win Rate:</span>
            <span className={`summary-value ${winRate >= 50 ? 'bullish' : 'bearish'}`}>
              {winRate.toFixed(1)}%
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Candles:</span>
            <span className="summary-value">{metrics50.total_count.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Market Bias:</span>
            <span className={`summary-value ${winRate >= 50 ? 'bullish' : 'bearish'}`}>
              {winRate >= 50 ? 'Bullish' : 'Bearish'}
            </span>
          </div>
        </div>
      </StandaloneSection>

      {/* 25th Percentile - Aggressive Targets */}
      <StandaloneSection title="25% ACHIEVABILITY — AGGRESSIVE TARGETS">
        <div className="quartile-explanation">
          <p>Only <strong>25% of candles</strong> achieved these movements. These are aggressive targets with lower probability but higher reward potential.</p>
        </div>
        <div className="quartile-row">
          <div className="quartile-card gain-card">
            <div className="card-label">Gain Target</div>
            <div className="card-value gain-value">{formatDollar(metrics25.percentile_gain, productId)}</div>
            <div className="card-percentage gain-pct">{metrics25.gain_pct_of_price?.toFixed(3)}%</div>
          </div>
          <div className="quartile-card loss-card">
            <div className="card-label">Pullback Depth</div>
            <div className="card-value loss-value">{formatDollar(metrics25.percentile_loss, productId)}</div>
            <div className="card-percentage loss-pct">{metrics25.loss_pct_of_price?.toFixed(3)}%</div>
          </div>
        </div>
      </StandaloneSection>

      {/* 50th Percentile - Median */}
      <StandaloneSection title="50% ACHIEVABILITY — MEDIAN (BALANCED)">
        <div className="quartile-explanation">
          <p><strong>50% of candles</strong> achieved these movements. This is the median - a balanced view of typical market behavior.</p>
        </div>
        <div className="quartile-row">
          <div className="quartile-card gain-card">
            <div className="card-label">Gain Target</div>
            <div className="card-value gain-value">{formatDollar(metrics50.percentile_gain, productId)}</div>
            <div className="card-percentage gain-pct">{metrics50.gain_pct_of_price?.toFixed(3)}%</div>
          </div>
          <div className="quartile-card loss-card">
            <div className="card-label">Pullback Depth</div>
            <div className="card-value loss-value">{formatDollar(metrics50.percentile_loss, productId)}</div>
            <div className="card-percentage loss-pct">{metrics50.loss_pct_of_price?.toFixed(3)}%</div>
          </div>
        </div>
      </StandaloneSection>

      {/* 75th Percentile - Conservative Targets */}
      <StandaloneSection title="75% ACHIEVABILITY — CONSERVATIVE TARGETS">
        <div className="quartile-explanation">
          <p><strong>75% of candles</strong> achieved these movements. These are conservative, high-probability targets with lower reward expectations.</p>
        </div>
        <div className="quartile-row">
          <div className="quartile-card gain-card">
            <div className="card-label">Gain Target</div>
            <div className="card-value gain-value">{formatDollar(metrics75.percentile_gain, productId)}</div>
            <div className="card-percentage gain-pct">{metrics75.gain_pct_of_price?.toFixed(3)}%</div>
          </div>
          <div className="quartile-card loss-card">
            <div className="card-label">Pullback Depth</div>
            <div className="card-value loss-value">{formatDollar(metrics75.percentile_loss, productId)}</div>
            <div className="card-percentage loss-pct">{metrics75.loss_pct_of_price?.toFixed(3)}%</div>
          </div>
        </div>
      </StandaloneSection>

      {/* Trading Insights */}
      <StandaloneSection title="💡 TRADING INSIGHTS">
        <div className="insight-explanation">
          <p className="explanation-header">
            <strong>How to Use These Quartiles</strong>
          </p>
          <p>
            Compare the three achievability levels to understand the range of probable outcomes.
            <strong> 25% = aggressive</strong>, <strong>50% = balanced</strong>, <strong>75% = conservative</strong>.
          </p>
        </div>
        <div className="insight-text">
          <p>
            <strong>Ladder Spacing Strategy:</strong>
          </p>
          <ul>
            <li><strong>Aggressive:</strong> Space buy orders {formatDollar(metrics25.percentile_loss, productId)} apart ({metrics25.loss_pct_of_price?.toFixed(2)}%) - captures larger pullbacks (25% frequency)</li>
            <li><strong>Balanced:</strong> Space buy orders {formatDollar(metrics50.percentile_loss, productId)} apart ({metrics50.loss_pct_of_price?.toFixed(2)}%) - median pullback depth</li>
            <li><strong>Conservative:</strong> Space buy orders {formatDollar(metrics75.percentile_loss, productId)} apart ({metrics75.loss_pct_of_price?.toFixed(2)}%) - catches most pullbacks (75% frequency)</li>
          </ul>
          <p>
            <strong>Profit Target Strategy:</strong>
          </p>
          <ul>
            <li><strong>Aggressive:</strong> {formatDollar(metrics25.percentile_gain, productId)} ({metrics25.gain_pct_of_price?.toFixed(2)}%) - lower hit rate, higher reward</li>
            <li><strong>Balanced:</strong> {formatDollar(metrics50.percentile_gain, productId)} ({metrics50.gain_pct_of_price?.toFixed(2)}%) - median target</li>
            <li><strong>Conservative:</strong> {formatDollar(metrics75.percentile_gain, productId)} ({metrics75.gain_pct_of_price?.toFixed(2)}%) - higher hit rate, lower reward</li>
          </ul>
          <p>
            <strong>Market Context:</strong> With a {winRate.toFixed(1)}% win rate, the market shows a <strong>{winRate >= 50 ? 'bullish' : 'bearish'} bias</strong>.
            {winRate >= 50
              ? ' Consider using conservative targets on longs and aggressive stops on shorts.'
              : ' Consider using conservative targets on shorts and aggressive stops on longs.'}
          </p>
        </div>
      </StandaloneSection>

      {/* Timeframe Selection Guide */}
      <StandaloneSection title="📊 TIMEFRAME SELECTION GUIDE">
        <div className="timeframe-guide">
          <div className="guide-content">
            <div className="guide-item">
              <strong>1m - 5m (Scalping):</strong> Ultra-short trades lasting seconds to minutes. Best for active traders monitoring positions constantly. Requires quick decisions and tight risk management.
            </div>
            <div className="guide-item">
              <strong>15m - 30m (Intraday):</strong> Short-term trades lasting minutes to a few hours. Good balance for active trading without constant monitoring. Captures intraday momentum shifts.
            </div>
            <div className="guide-item">
              <strong>1h - 4h (Swing Trading):</strong> Medium-term trades lasting hours to days. Suitable for part-time traders. Filters out short-term noise while capturing meaningful trends.
            </div>
            <div className="guide-item">
              <strong>1d (Position Trading):</strong> Longer-term trades lasting days to weeks. Best for low-frequency, high-conviction positions. Focuses on major market structure and trends.
            </div>
          </div>
        </div>
      </StandaloneSection>
        </>
        );
      })()}

      {/* TODO: Add charts for Regime Transitions, Volatility Percentile, and Momentum */}
      {/* These will be implemented in the next iteration */}
    </div>
  );
};

export default PriceIntelligencePanel;

